'use client';

// Two-step login (backend auth/mfa.py) — only ever shown to staff accounts (admin / agency), so the
// copy is Polish like the rest of the admin panel.

import React, { useEffect, useState } from 'react';
import { signIn } from 'next-auth/react';
import { ArrowLeft, Check, Copy, Download, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Checkbox } from '@/core/components/ui/checkbox';
import { Input } from '@/core/components/ui/input';
import { DynamicQRCode } from '@/core/components/ui/QRCode';
import { invalidateClientSessionCache } from '@/core/api/auth-helper';

export type MfaChallenge = { kind: 'verify' | 'setup'; token: string };

export interface MfaSetup {
    secret: string;
    otpauth_url: string;
    setup_token: string;
}

const GENERIC_ERROR = 'Coś poszło nie tak. Spróbuj ponownie.';
const SESSION_EXPIRED = 'Sesja logowania wygasła. Zaloguj się ponownie.';

// Errors travel as "MFA_<CODE>|<payload>" — see authorize() in core/config/auth.ts and postMfa below.
function splitMfaError(error: string): [string, string] {
    const i = error.indexOf('|');
    return i === -1 ? [error, ''] : [error.slice(0, i), error.slice(i + 1)];
}

/** The signIn() error of a correct password when the account needs a second step, else null. */
export function mfaChallengeFromError(error?: string | null): MfaChallenge | null {
    if (!error) return null;
    const [code, payload] = splitMfaError(error);
    if (code === 'MFA_REQUIRED' && payload) return { kind: 'verify', token: payload };
    if (code === 'MFA_SETUP_REQUIRED' && payload) return { kind: 'setup', token: payload };
    return null;
}

export function mfaErrorMessage(error: string): { message: string; fatal: boolean } {
    const [code, payload] = splitMfaError(error);
    switch (code) {
        case 'MFA_INVALID_CODE':
            return { message: payload || 'Nieprawidłowy kod. Spróbuj ponownie.', fatal: false };
        case 'MFA_LOCKED':
            return { message: payload || 'Zbyt wiele nieudanych prób. Konto jest tymczasowo zablokowane.', fatal: true };
        case 'MFA_SESSION_EXPIRED':
        case 'MFA_SETUP_EXPIRED':
            return { message: payload || SESSION_EXPIRED, fatal: true };
        default:
            return { message: payload || GENERIC_ERROR, fatal: false };
    }
}

/** POST to backend /auth/mfa/* through the /api proxy, which adds the session token when there is one. */
export async function postMfa<T = any>(path: string, body: object): Promise<T> {
    const res = await fetch(`/api/auth/mfa${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const detail = data?.detail;
        const code = detail && typeof detail === 'object' && detail.code ? detail.code : 'MFA_ERROR';
        const message = detail && typeof detail === 'object' ? detail.message : '';
        throw new Error(`${code}|${message || ''}`);
    }
    return data;
}

function CodeField({ id, value, onChange, recovery = false, disabled, autoFocus }: {
    id: string;
    value: string;
    onChange: (value: string) => void;
    recovery?: boolean;
    disabled?: boolean;
    autoFocus?: boolean;
}) {
    return (
        <Input
            id={id}
            value={value}
            onChange={e => onChange(recovery ? e.target.value.slice(0, 16) : e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode={recovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoCapitalize="off"
            spellCheck={false}
            placeholder={recovery ? 'xxxxx-xxxxx' : '000000'}
            disabled={disabled}
            autoFocus={autoFocus}
            className="h-12 bg-white text-center font-mono text-xl tracking-[0.3em] placeholder:tracking-[0.3em]"
        />
    );
}

function ErrorText({ children }: { children: React.ReactNode }) {
    return <p role="alert" className="text-sm font-medium text-red-600">{children}</p>;
}

/** QR code + manual key + first code — pairing an authenticator app. */
export function TotpPairing({ setup, onConfirm, busy, error }: {
    setup: MfaSetup;
    onConfirm: (code: string) => void;
    busy?: boolean;
    error?: string;
}) {
    const [code, setCode] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (error) setCode('');
    }, [error]);

    const copyKey = async () => {
        try {
            await navigator.clipboard.writeText(setup.secret);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard blocked — the key is on screen anyway */ }
    };

    return (
        <div className="space-y-5">
            <ol className="list-decimal space-y-1.5 pl-5 text-sm text-slate-600">
                <li>Zainstaluj na telefonie aplikację uwierzytelniającą, np. Google Authenticator, Microsoft Authenticator albo Authy.</li>
                <li>Zeskanuj w niej kod QR (lub wpisz klucz ręcznie).</li>
                <li>Wpisz 6-cyfrowy kod, który pokaże aplikacja.</li>
            </ol>
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
                <DynamicQRCode value={setup.otpauth_url} size={164} className="shrink-0" />
                <div className="min-w-0 space-y-2 text-center sm:text-left">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Klucz do wpisania ręcznie</p>
                    <p className="break-all font-mono text-sm text-slate-900">{setup.secret.match(/.{1,4}/g)?.join(' ')}</p>
                    <Button type="button" variant="outline" size="sm" onClick={copyKey}>
                        {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
                        {copied ? 'Skopiowano' : 'Kopiuj klucz'}
                    </Button>
                </div>
            </div>
            <form
                className="space-y-2"
                onSubmit={e => {
                    e.preventDefault();
                    if (code.length === 6) onConfirm(code);
                }}
            >
                <label htmlFor="mfa-pairing-code" className="text-sm font-semibold text-slate-800">Kod z aplikacji</label>
                <div className="flex gap-2">
                    <CodeField id="mfa-pairing-code" value={code} onChange={setCode} disabled={busy} />
                    <Button type="submit" className="h-12 px-5" disabled={busy || code.length !== 6}>
                        {busy && <Loader2 className="animate-spin" aria-hidden />}
                        Potwierdź
                    </Button>
                </div>
                {error && <ErrorText>{error}</ErrorText>}
            </form>
        </div>
    );
}

/** One-time display of recovery codes, with copy/download and an explicit "saved" confirmation. */
export function RecoveryCodes({ codes, onDone, doneLabel, busy }: {
    codes: string[];
    onDone: () => void;
    doneLabel: string;
    busy?: boolean;
}) {
    const [saved, setSaved] = useState(false);
    const [copied, setCopied] = useState(false);
    const text = codes.join('\n');

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* clipboard blocked — download still works */ }
    };

    const download = () => {
        const content = `Kody zapasowe do logowania (${window.location.host})\nKażdy kod działa tylko raz.\n\n${text}\n`;
        const url = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `kody-zapasowe-${window.location.hostname}.txt`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-4">
            <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                Zapisz te kody w bezpiecznym miejscu. Każdy działa jeden raz — użyjesz go zamiast kodu z aplikacji, gdy nie będziesz mieć telefonu. <strong>Nie pokażemy ich ponownie.</strong>
            </p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4 font-mono text-sm text-slate-900">
                {codes.map(code => <li key={code}>{code}</li>)}
            </ul>
            <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={copy}>
                    {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
                    {copied ? 'Skopiowano' : 'Kopiuj'}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={download}>
                    <Download aria-hidden /> Pobierz plik .txt
                </Button>
            </div>
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-700">
                <Checkbox checked={saved} onCheckedChange={value => setSaved(value === true)} />
                Kody są zapisane w bezpiecznym miejscu
            </label>
            <Button type="button" className="w-full" disabled={!saved || busy} onClick={onDone}>
                {busy && <Loader2 className="animate-spin" aria-hidden />}
                {doneLabel}
            </Button>
        </div>
    );
}

function StepShell({ title, intro, onCancel, children }: {
    title: string;
    intro?: string;
    onCancel: () => void;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-5 text-left">
            <div className="space-y-2">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white">
                    <ShieldCheck size={22} aria-hidden />
                </div>
                <h2 className="text-xl font-bold tracking-tight text-slate-900">{title}</h2>
                {intro && <p className="text-sm text-slate-600">{intro}</p>}
            </div>
            {children}
            <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900"
            >
                <ArrowLeft size={14} aria-hidden /> Wróć do logowania
            </button>
        </div>
    );
}

function VerifyStep({ token, onCancel, onSuccess }: { token: string; onCancel: () => void; onSuccess?: () => void }) {
    const [code, setCode] = useState('');
    const [useRecovery, setUseRecovery] = useState(false);
    const [error, setError] = useState('');
    const [fatal, setFatal] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (value: string) => {
        if (loading || !value) return;
        setLoading(true);
        setError('');
        try {
            const result = await signIn('credentials', { redirect: false, mfaToken: token, code: value });
            if (result?.error) {
                const { message, fatal } = mfaErrorMessage(result.error);
                setError(message);
                setFatal(fatal);
                setCode('');
            } else {
                invalidateClientSessionCache();
                onSuccess?.();
            }
        } catch {
            setError(GENERIC_ERROR);
        } finally {
            setLoading(false);
        }
    };

    const onCodeChange = (value: string) => {
        setCode(value);
        if (!useRecovery && value.length === 6) submit(value);
    };

    return (
        <StepShell
            title="Weryfikacja dwuetapowa"
            intro={useRecovery
                ? 'Wpisz jeden z kodów zapasowych zapisanych przy włączaniu weryfikacji.'
                : 'Otwórz aplikację uwierzytelniającą na telefonie i wpisz 6-cyfrowy kod.'}
            onCancel={onCancel}
        >
            {fatal ? (
                <ErrorText>{error}</ErrorText>
            ) : (
                <form className="space-y-3" onSubmit={e => { e.preventDefault(); submit(code); }}>
                    <label htmlFor="mfa-login-code" className="sr-only">{useRecovery ? 'Kod zapasowy' : 'Kod z aplikacji'}</label>
                    <CodeField id="mfa-login-code" value={code} onChange={onCodeChange} recovery={useRecovery} disabled={loading} autoFocus />
                    {error && <ErrorText>{error}</ErrorText>}
                    <Button type="submit" className="h-11 w-full" disabled={loading || !code || (!useRecovery && code.length !== 6)}>
                        {loading && <Loader2 className="animate-spin" aria-hidden />}
                        Zaloguj się
                    </Button>
                    <button
                        type="button"
                        onClick={() => { setUseRecovery(v => !v); setCode(''); setError(''); }}
                        className="block text-sm font-medium text-slate-600 underline-offset-4 hover:text-slate-900 hover:underline"
                    >
                        {useRecovery ? 'Użyj kodu z aplikacji' : 'Nie masz telefonu? Użyj kodu zapasowego'}
                    </button>
                </form>
            )}
        </StepShell>
    );
}

function FirstPairingStep({ token, onCancel, onSuccess }: { token: string; onCancel: () => void; onSuccess?: () => void }) {
    const [setup, setSetup] = useState<MfaSetup | null>(null);
    const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
    const [ticket, setTicket] = useState('');
    const [error, setError] = useState('');
    const [fatal, setFatal] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        postMfa<MfaSetup>('/login-setup', { mfa_token: token })
            .then(data => { if (!cancelled) setSetup(data); })
            .catch((e: Error) => {
                if (cancelled) return;
                setError(mfaErrorMessage(e.message).message);
                setFatal(true);
            });
        return () => { cancelled = true; };
    }, [token]);

    const confirm = async (code: string) => {
        if (!setup) return;
        setBusy(true);
        setError('');
        try {
            const data = await postMfa<{ recovery_codes: string[]; login_ticket: string }>('/login-setup/confirm', {
                mfa_token: token, setup_token: setup.setup_token, code,
            });
            setRecoveryCodes(data.recovery_codes);
            setTicket(data.login_ticket);
        } catch (e) {
            const { message, fatal } = mfaErrorMessage((e as Error).message);
            setError(message);
            setFatal(fatal);
        } finally {
            setBusy(false);
        }
    };

    const finish = async () => {
        setBusy(true);
        try {
            const result = await signIn('credentials', { redirect: false, loginTicket: ticket });
            if (result?.error) {
                setError(mfaErrorMessage(result.error).message);
                setFatal(true);
            } else {
                invalidateClientSessionCache();
                onSuccess?.();
            }
        } catch {
            setError(GENERIC_ERROR);
        } finally {
            setBusy(false);
        }
    };

    return (
        <StepShell
            title={recoveryCodes ? 'Zapisz kody zapasowe' : 'Włącz weryfikację dwuetapową'}
            intro={recoveryCodes
                ? 'Aplikacja jest sparowana. Jeszcze jeden krok przed wejściem do panelu.'
                : 'Ten sklep wymaga weryfikacji dwuetapowej dla kont administratorów. Sparuj aplikację, aby się zalogować.'}
            onCancel={onCancel}
        >
            {fatal ? (
                <ErrorText>{error}</ErrorText>
            ) : recoveryCodes ? (
                <>
                    <RecoveryCodes codes={recoveryCodes} onDone={finish} doneLabel="Przejdź do panelu" busy={busy} />
                    {error && <ErrorText>{error}</ErrorText>}
                </>
            ) : setup ? (
                <TotpPairing setup={setup} onConfirm={confirm} busy={busy} error={error} />
            ) : (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" aria-label="Ładowanie" /></div>
            )}
        </StepShell>
    );
}

/** Replaces the login form after a correct password when the account uses two-step login. */
export function MfaLoginStep({ challenge, onCancel, onSuccess }: {
    challenge: MfaChallenge;
    onCancel: () => void;
    onSuccess?: () => void;
}) {
    return challenge.kind === 'verify'
        ? <VerifyStep token={challenge.token} onCancel={onCancel} onSuccess={onSuccess} />
        : <FirstPairingStep token={challenge.token} onCancel={onCancel} onSuccess={onSuccess} />;
}
